/**
 * Validator + FormValidator — port 1:1 din legacy common.js lines 1700-2282.
 * Suporta rule-uri: nn, min, max, vmin, vmax, len, int, uint, num, alpha, eq,
 * eqr, eqf, mail, url, date, cnp.
 *
 * Apeleaza `intData.error.*` pentru mesaje (incarcate de Resort.setIntData()).
 */

import { $ } from '../core/selector.js';
import { isset, trigger } from '../core/util.js';

interface ValidatorEvents {
    init?:        (fields: Record<string, HTMLElement | HTMLElement[]>) => void;
    valid?:       (entry: Record<string, unknown>) => Record<string, string>;
    return_true?: (entry: Record<string, unknown>) => boolean;
    done?:        (resp: number, data: unknown) => void;
}

interface ValidatorOpts {
    submit?:     boolean;
    reset_form?: boolean;
    modal?:      boolean;
    loader?:     boolean;
}

type FormEl = HTMLFormElement & { elements: HTMLFormControlsCollection };

interface ErrorMessages {
    req:        string;
    min_len:    string;
    max_len:    string;
    num:        string;
    min_val:    string;
    max_val:    string;
    len:        string;
    num_int:    string;
    alpha:      string;
    wrong:      string;
    inv:        string;
    date:       string;
    date_hour:  string;
}

function getIntDataErr(): ErrorMessages {
    return ((window as unknown as { intData?: { error?: ErrorMessages } }).intData?.error ?? {}) as ErrorMessages;
}

export class Validator {
    target!: FormEl;
    events!: ValidatorEvents;
    entry: Record<string, unknown> = {};
    vars: Record<string, unknown> = {};

    getData(): Record<string, unknown> {
        const obj: Record<string, unknown> = {};

        [...this.target.elements].forEach((el) => {
            const input = el as HTMLInputElement;

            if (input.type === 'file' && input.name) {
                obj['files'] = input.files;
                return;
            }
            if (!input.name || input.type === 'button' || input.type === 'submit') return;

            let value: string;
            let rewriteValue: string | null = null;

            if (isset(input.dataset['rewriteValue'])) {
                value = input.dataset['rewriteValue']!;
                rewriteValue = input.value.trim();
            } else {
                value = input.value.trim();
            }

            switch (input.type) {
                case 'radio': {
                    if (!(input.name in obj)) obj[input.name] = '';
                    if (input.checked) obj[input.name] = value;
                    break;
                }
                case 'checkbox': {
                    if (!(input.name in obj) && !(`${input.name}[]` in obj)) {
                        obj[input.name] = '';
                    }
                    if (input.checked) {
                        if (obj[input.name] === '') {
                            obj[input.name] = value;
                        } else if (`${input.name}[]` in obj) {
                            (obj[`${input.name}[]`] as unknown[]).push(value);
                        } else {
                            obj[`${input.name}[]`] = [obj[input.name], value];
                            delete obj[input.name];
                        }
                    }
                    break;
                }
                case 'select-multiple': {
                    const sel = input as unknown as HTMLSelectElement;
                    const values: (string | number)[] = [];
                    for (let j = 0; j < sel.options.length; j++) {
                        const opt = sel.options[j]!;
                        if (opt.selected && opt.value !== '0') {
                            values.push(/^\d+$/.test(opt.value) ? parseInt(opt.value) : opt.value);
                        }
                    }
                    obj[`${input.name}[]`] = values;
                    break;
                }
                default: {
                    if (input.name in obj) {
                        obj[`${input.name}[]`] = [obj[input.name], value];
                        delete obj[input.name];
                    } else if (`${input.name}[]` in obj) {
                        (obj[`${input.name}[]`] as unknown[]).push(value);
                    } else {
                        obj[input.name] = value;
                        if (rewriteValue) {
                            obj[`${input.name}_value`] = rewriteValue;
                        }
                    }
                }
            }
        });

        return obj;
    }

    validEntry(input: HTMLInputElement, val: string, rule: string, arg: string): boolean {
        const err = getIntDataErr();

        switch (rule) {
            case 'nn':
                if (val === '' || val === '0') return this.inputError(input, err.req);
                break;
            case 'min':
                if (!val) return this.inputError(input, err.req);
                if (val.length < Number(arg)) return this.inputError(input, err.min_len.replace('%s', arg));
                break;
            case 'max':
                if (val.length > Number(arg)) return this.inputError(input, err.max_len.replace('%s', arg));
                break;
            case 'vmin':
                if (val === '') return this.inputError(input, err.req);
                if (isNaN(Number(val))) return this.inputError(input, err.num);
                if (parseFloat(val) < Number(arg)) return this.inputError(input, err.min_val.replace('%s', arg));
                break;
            case 'vmax':
                if (val === '') return this.inputError(input, err.req);
                if (isNaN(Number(val))) return this.inputError(input, err.num);
                if (parseFloat(val) > Number(arg)) return this.inputError(input, err.max_val.replace('%s', arg));
                break;
            case 'len':
                if (!val) return this.inputError(input, err.req);
                if (val.length !== Number(arg)) return this.inputError(input, err.len.replace('%s', arg));
                break;
            case 'int':
                if (!val) return this.inputError(input, err.req);
                if (!/^-?\d+$/.test(val)) return this.inputError(input, err.num_int);
                if (/^\d+$/.test(arg) && val.length > Number(arg)) return this.inputError(input, err.max_val.replace('%s', arg));
                break;
            case 'uint':
                if (!val) return this.inputError(input, err.req);
                if (!/^\d+$/.test(val)) return this.inputError(input, err.num_int);
                if (/^\d+$/.test(arg) && val.length > Number(arg)) return this.inputError(input, err.max_val.replace('%s', arg));
                break;
            case 'num':
                if (!val) return this.inputError(input, err.req);
                if (isNaN(Number(val))) return this.inputError(input, err.num);
                if (arg && !isNaN(Number(arg)) && Number(val) > Number(arg)) return this.inputError(input, err.max_val.replace('%s', arg));
                break;
            case 'alpha':
                if (!val) return this.inputError(input, err.req);
                if (!/^[a-z0-9]+(-[a-z0-9]+)*$/i.test(val)) return this.inputError(input, err.alpha);
                break;
            case 'eq':
                if (arg !== val) return this.inputError(input, err.wrong);
                break;
            case 'eqr':
                if (!new RegExp(`^(${arg})$`).test(val)) return this.inputError(input, err.wrong);
                break;
            case 'eqf':
                if (!val) return this.inputError(input, err.req);
                if (this.entry[arg] !== val) return this.inputError(input, err.wrong);
                break;
            case 'mail':
                if (!val) return this.inputError(input, err.req);
                if (!/^[_a-z0-9-]+(\.[_a-z0-9-]+)*@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,3})$/.test(val)) {
                    return this.inputError(input, err.inv);
                }
                if (arg && val.length > Number(arg)) return this.inputError(input, err.max_len.replace('%s', arg));
                break;
            case 'url':
                if (!val) return this.inputError(input, err.req);
                if (!/^(http|https):\/\/(.+)$/.test(val)) return this.inputError(input, err.inv);
                if (arg && val.length > Number(arg)) return this.inputError(input, err.max_len.replace('%s', arg));
                break;
            case 'date':
                if (!val) return this.inputError(input, err.req);
                if (arg === 'time') {
                    if (!/^(\d{2})-(\d{2})-(\d{4})(,?)( )(\d{2}):(\d{2}):(\d{2})$/.test(val)) {
                        return this.inputError(input, err.date_hour);
                    }
                } else if (!/^(\d{2})-(\d{2})-(\d{4})$/.test(val)) {
                    return this.inputError(input, err.date);
                }
                break;
            case 'cnp':
                if (!val) return this.inputError(input, err.req);
                if (!this.validCNP(val)) return this.inputError(input, err.inv);
                break;
        }
        return true;
    }

    inputError(input: HTMLInputElement, emsg: string): boolean {
        if (input.type === 'checkbox' || input.type === 'radio') {
            (input.parentNode as Element | null)?.classList.add('sp-has-error');
        } else {
            input.classList.add('sp-input-error');
        }

        const form = (input as unknown as Node).parents('form') as HTMLElement | null;
        if (!form) return false;

        let ebox = $('.sp-ebox') as HTMLElement | null;
        if (!ebox) {
            ebox = $('<div class="sp-ebox" />') as HTMLElement;
            const close = $('<a class="close-popup sp-ebox-close"><div class="container-close"><div class="border border-1"></div><div class="border border-2"></div></div><div class="close-btn">ÎNCHIDE</div></a>') as HTMLElement;
            if (!form.classList.contains('popup-fixed')) {
                form.css({ position: 'relative', overflow: 'visible' });
            }
            ebox.append(close);
            form.append(ebox);
            close.on('click', () => ebox?.remove());
        }

        const p = $(`<p><strong>${input.getAttribute('title') ?? ''}</strong>: ${emsg}</p>`) as HTMLElement;
        ebox.append(p);

        setTimeout(() => {
            (ebox as HTMLElement).style.height = `${(ebox as HTMLElement).offsetHeight}px`;
            (ebox as HTMLElement).classList.add('fixed');
        }, 0);

        return false;
    }

    validCNP(value: string): boolean {
        const hashTable = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
        if (value.length !== 13) return false;
        const cnp: number[] = [];
        let hashResult = 0;
        for (let i = 0; i < 13; i++) {
            cnp[i] = parseInt(value.charAt(i), 10);
            if (isNaN(cnp[i]!)) return false;
            if (i < 12) hashResult += cnp[i]! * hashTable[i]!;
        }
        hashResult %= 11;
        if (hashResult === 10) hashResult = 1;
        let year = cnp[1]! * 10 + cnp[2]!;
        switch (cnp[0]) {
            case 1: case 2: year += 1900; break;
            case 3: case 4: year += 1800; break;
            case 5: case 6: year += 2000; break;
            case 7: case 8: case 9:
                year += 2000;
                if (year > parseInt(String(new Date().getFullYear() - 1900), 10) - 14) year -= 100;
                break;
            default: return false;
        }
        if (year < 1800 || year > 2099) return false;
        return cnp[12] === hashResult;
    }

    valid(): boolean {
        const that = this;
        let ret = true;

        ($('.sp-ebox') as HTMLElement | null)?.remove();

        this.target.find('.sp-label-error', (item: Element) => {
            item.remove();
            return true;
        });
        this.target.find('.sp-input-error', (item: Element) => {
            item.classList.remove('sp-input-error', 'sp-has-error');
            return true;
        });

        const rules = this.events.valid?.(this.entry) ?? {};
        const err = getIntDataErr();

        for (const k in rules) {
            const els = (this.target.find(`[name="${k}"]`, true) as NodeListOf<HTMLInputElement>);
            const first = els[0];
            if (first && (first.type === 'checkbox' || first.type === 'radio')) {
                if (!first.checked) {
                    ret = that.inputError(first, err.req);
                }
            } else {
                els.forEach((input) => {
                    let val = '';
                    if (input.type === 'checkbox') {
                        val = input.checked ? input.value : '';
                    } else {
                        const tmp = $(`<p>${input.value}</p>`) as HTMLElement;
                        val = tmp.textContent?.trim() ?? '';
                    }
                    if (!val && /pass\(\)/.test(rules[k]!)) return;

                    const matches = rules[k]!.match(/(\w+)\((\w*)\)/g);
                    if (matches) {
                        for (const match of matches) {
                            const c = match.match(/(\w+)\((\w*)\)/);
                            if (!c) continue;
                            if (!that.validEntry(input, val, c[1]!, c[2] ?? '')) ret = false;
                        }
                    }
                });
            }
        }
        return ret;
    }
}

export class FormValidator extends Validator {
    opts!: ValidatorOpts;

    constructor(target: FormEl, events?: ValidatorEvents, opts?: ValidatorOpts) {
        super();
        this.target = target;
        if (target.data('form')) return;
        target.data('form', this);

        this.events = events ?? {};
        this.opts   = { submit: false, reset_form: false, ...(opts ?? {}) };

        this.init();
        this.setEvents();
    }

    init(): void {
        const target: Record<string, HTMLElement | HTMLElement[]> = {};
        [...this.target.elements].forEach((el) => {
            const input = el as HTMLInputElement;
            if (!input.name || input.type === 'button' || input.type === 'submit') return;
            if (target[input.name]) {
                if (!Array.isArray(target[input.name])) {
                    target[input.name] = [];
                }
                (target[input.name] as HTMLElement[]).push(input);
            } else {
                target[input.name] = input;
            }
        });
        trigger(this.events.init, [target]);
    }

    setEvents(): void {
        const that = this;

        this.target.on('click.ui', '.sp-has-error', function (this: Element) {
            const inp = this.find('input') as HTMLInputElement | null;
            if (!inp) return;
            that.target.find(`input[name="${inp.name}"`, (item: Element) => {
                (item.parentNode as Element | null)?.classList.remove('sp-has-error');
                return true;
            });
        });

        this.target.on('click.form', 'input, select, textarea', function (this: Element) {
            this.classList.remove('sp-input-error');
            if ((this.nextElementSibling as Element | null)?.matches('.sp-label-error')) {
                this.nextElementSibling?.remove();
            }
            ($('.sp-ebox') as HTMLElement | null)?.remove();
        });

        this.target.on('submit.form', function (this: Element, e: Event) {
            if (!that.opts.submit) e.preventDefault();
            that.entry = Object.assign({}, that.getData(), that.vars);
            const isValidFn = that.events.valid && !that.valid();
            const returnTrueFn = typeof that.events.return_true === 'function' && that.events.return_true(that.entry);
            if (isValidFn || returnTrueFn) {
                e.preventDefault();
                if (that.opts.reset_form) that.target.reset();
            }
        });
    }

    destroy(): HTMLFormElement {
        this.target.off('submit.form click.form click.ui');
        return this.target;
    }
}

Node.prototype.FormValidator = function (this: Node, events?: ValidatorEvents, opts?: ValidatorOpts) {
    new FormValidator(this as FormEl, events, opts);
    return this;
} as Node['FormValidator'];
